/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import MDBox from "admin/components/MDBox";
import MDInput from "admin/components/MDInput";
import MDButton from "admin/components/MDButton";
import MDTypography from "admin/components/MDTypography";

import { MapContainer, TileLayer, Marker, CircleMarker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const MAP_LAYERS = {
    dark: { url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}@2x.png", label: "Dark", filter: "brightness(1.4) contrast(1.3)" },
    satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", label: "Satellite", filter: "none" },
    streets: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", label: "Streets", filter: "none" },
    detailed: { url: "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png", label: "Detailed", filter: "none" },
};

function LayerSwitcher() {
    const map = useMap();
    const [active, setActive] = useState("dark");
    const layerRef = useRef(null);

    useEffect(() => {
        const info = MAP_LAYERS[active];
        if (layerRef.current) map.removeLayer(layerRef.current);
        layerRef.current = L.tileLayer(info.url, { maxZoom: 19 });
        layerRef.current.addTo(map);
        map.getContainer().style.filter = info.filter;
    }, [active]);

    return (
        <div style={{ position: "absolute", top: 8, right: 8, zIndex: 1000, display: "flex", gap: 2 }}>
            {Object.entries(MAP_LAYERS).map(([key, info]) => (
                <div key={key} onClick={() => setActive(key)}
                    style={{
                        padding: "3px 8px", fontSize: "9px", fontFamily: "Inter, sans-serif",
                        background: active === key ? "rgba(194,151,23,0.9)" : "rgba(10,10,10,0.85)",
                        color: active === key ? "#000" : "#aaa",
                        border: "1px solid " + (active === key ? "#C29717" : "rgba(255,255,255,0.15)"),
                        borderRadius: "2px", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.5px",
                        fontWeight: active === key ? 600 : 400
                    }}>
                    {info.label}
                </div>
            ))}
        </div>
    );
}

function FitBoundsOnce({ markers }) {
    const map = useMap();
    const fitted = useRef(false);
    useEffect(() => {
        if (!fitted.current && markers.length > 0) {
            fitted.current = true;
            if (markers.length > 1) {
                map.fitBounds(markers.map(m => [m.lat, m.lng]), { padding: [40, 40] });
            } else {
                map.setView([markers[0].lat, markers[0].lng], 10);
            }
        }
    }, []);
    return null;
}
import axios from "axios";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

const MODAL_API = process.env.REACT_APP_APIHOST || 'http://localhost:3033';

const defaultIcon = L.icon({
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

const COUNTRIES_LIST = [
    "Syria", "Lebanon", "Palestine", "Jordan", "Iraq", "Yemen", "Egypt",
    "Saudi Arabia", "UAE", "Kuwait", "Bahrain", "Qatar", "Oman",
    "Turkey", "Iran", "Afghanistan", "Pakistan", "Libya", "Tunisia",
    "Algeria", "Morocco", "Sudan", "Somalia",
];

const VIOLENCE_TYPES = [
    "Missile Attack", "Drone Attack", "Bombing", "Airstrike", "Shelling / Artillery",
    "Suicide Attack", "Armed Assault", "Ambush", "Car Bombing (VBIED)", "IED / Roadside Bomb",
    "Assassination / Targeted Killing", "Kidnapping / Hostage Taking", "Maritime Attack",
    "Cyber Attack", "Chemical / Biological Attack", "Insurgency", "Terrorist Activity",
    "Repression", "Atrocities", "Ethnic / Sectarian Violence", "Foreign Military Engagement",
    "Border Clash", "Naval Confrontation", "Siege / Blockade", "Sabotage", "Riot / Civil Unrest", "Other",
];

const SOURCE_OPTIONS = ["Manual Entry", "CSV Imported", "AI (ChatGPT)"];
const STATUS_OPTIONS = ["Published", "Unpublished"];
const IMAGE_CATEGORIES = ["Incident Image", "Satellite Image", "Other"];


const COUNTRY_TIMEZONES = {
    "Syria": "Asia/Damascus", "Lebanon": "Asia/Beirut", "Palestine": "Asia/Hebron",
    "Jordan": "Asia/Amman", "Iraq": "Asia/Baghdad", "Yemen": "Asia/Aden",
    "Egypt": "Africa/Cairo", "Saudi Arabia": "Asia/Riyadh", "UAE": "Asia/Dubai",
    "Kuwait": "Asia/Kuwait", "Bahrain": "Asia/Bahrain", "Qatar": "Asia/Qatar",
    "Oman": "Asia/Muscat", "Turkey": "Europe/Istanbul", "Iran": "Asia/Tehran",
    "Afghanistan": "Asia/Kabul", "Pakistan": "Asia/Karachi", "Libya": "Africa/Tripoli",
    "Tunisia": "Africa/Tunis", "Algeria": "Africa/Algiers", "Morocco": "Africa/Casablanca",
    "Sudan": "Africa/Khartoum", "Somalia": "Africa/Mogadishu",
};

function getLocalDateTime(country) {
    const tz = COUNTRY_TIMEZONES[country];
    if (!tz) return '';
    try {
        const now = new Date();
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        }).formatToParts(now);
        const get = (type) => parts.find(p => p.type === type)?.value || '';
        return get('year') + '-' + get('month') + '-' + get('day') + 'T' + get('hour') + ':' + get('minute');
    } catch(e) { return ''; }
}

function LocationMarker({ position, onPositionChange, setLoading }) {
    const markerRef = React.useRef(null);
    useMapEvents({
        click(e) {
            const { lat, lng } = e.latlng;
            onPositionChange([lat, lng]);
            setLoading(true);
            fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
                .then(r => r.json()).catch(() => {}).finally(() => setLoading(false));
        },
    });
    const handleDragEnd = React.useCallback(() => {
        const marker = markerRef.current;
        if (marker) {
            const { lat, lng } = marker.getLatLng();
            onPositionChange([lat, lng]);
        }
    }, [onPositionChange]);
    return position ? <Marker position={position} icon={defaultIcon} draggable={true} ref={markerRef} eventHandlers={{ dragend: handleDragEnd }} /> : null;
}

function EventModal({ open, onClose, event, onSave }) {
    const [tab, setTab] = useState(0);
    const [formData, setFormData] = useState({
        conflictName: "", country: null, typeOfViolence: "", description: "",
        typeOfSource: "Manual Entry", exactSources: "", dateStart: "", dateEnd: "",
        sideA: "", sideB: "", totalDeaths: 0, status: "Unpublished",
        highImpactIntel: false,
        latitude: "", longitude: "",
        technicalReportUrl: "",
    });

    const [images, setImages] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);
    const [pendingCategory, setPendingCategory] = useState("Incident Image");
    const [eventWeapons, setEventWeapons] = useState([]);
    const [weaponSearch, setWeaponSearch] = useState('');
    const [weaponResults, setWeaponResults] = useState([]);
    const [showAddWeapon, setShowAddWeapon] = useState(false);
    const [newWeapon, setNewWeapon] = useState({name:'',designation:'',manufacturer:'',operator:'',role:'offensive'});
    const weaponSearchTimeout = React.useRef(null);
    const [mapCenter, setMapCenter] = useState([33.5138, 36.2765]);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [mapKey, setMapKey] = useState(0);
    const [eventMarkers, setEventMarkers] = useState([]);
    const [locSearch, setLocSearch] = useState("");
    const [locSuggestions, setLocSuggestions] = useState([]);
    const [searchingLoc, setSearchingLoc] = useState(false);
    const locSearchTimeout = React.useRef(null);

    useEffect(() => {
        if (event) {
            const deaths = (event.deathA || 0) + (event.deathB || 0) + (event.deathCivilians || 0) + (event.deathUnknown || 0);
            setFormData({
                conflictName: event.conflictName || "", country: event.country || null,
                typeOfViolence: event.typeOfViolence || "", description: event.description || "",
                typeOfSource: event.source || "Manual Entry", exactSources: event.exactSources || "",
                dateStart: event.dateStart || "", dateEnd: event.dateEnd || "",
                sideA: event.sideA || "", sideB: event.sideB || "",
                totalDeaths: deaths, status: event.status || "Unpublished",
                highImpactIntel: event.highImpactIntel || false,
                latitude: event.latitude || "", longitude: event.longitude || "",
                technicalReportUrl: event.technicalReportUrl || "",
            });
            setImages(event.images || []);
            setEventWeapons(event.weapons || []);
            setEventMarkers(event.markers && event.markers.length > 0 ? event.markers.map(m => ({lat:parseFloat(m.lat),lng:parseFloat(m.lng),label:m.label||''})) : (event.latitude && event.longitude ? [{lat:parseFloat(event.latitude),lng:parseFloat(event.longitude),label:''}] : []));
            if (event.markers && event.markers.length > 1) {
                const validMks = event.markers.filter(m => m && m.lat && m.lng);
                if (validMks.length > 0) {
                    const avgLat = validMks.reduce((s, m) => s + parseFloat(m.lat), 0) / validMks.length;
                    const avgLng = validMks.reduce((s, m) => s + parseFloat(m.lng), 0) / validMks.length;
                    setMapCenter([avgLat, avgLng]); setMarkerPosition(null); setMapKey(p => p + 1);
                }
            } else if (event.latitude && event.longitude) {
                const pos = [parseFloat(event.latitude), parseFloat(event.longitude)];
                setMapCenter(pos); setMarkerPosition(pos); setMapKey(p => p + 1);
            }
        } else {
            setFormData({
                conflictName: "", country: null, typeOfViolence: "", description: "",
                typeOfSource: "Manual Entry", exactSources: "", dateStart: "", dateEnd: "",
                sideA: "", sideB: "", totalDeaths: 0, status: "Unpublished",
        highImpactIntel: false,
                latitude: "", longitude: "", technicalReportUrl: "",
            });
            setImages([]); setEventWeapons([]); setEventMarkers([]); setMarkerPosition(null); setSearchQuery("");
            setMapCenter([33.5138, 36.2765]); setMapKey(p => p + 1);
            // Default dateStart to now
            const now = new Date();
            const pad = (n) => String(n).padStart(2,'0');
            const defaultDate = now.getFullYear()+'-'+pad(now.getMonth()+1)+'-'+pad(now.getDate())+'T'+pad(now.getHours())+':'+pad(now.getMinutes());
            setFormData(prev => ({...prev, dateStart: defaultDate}));
        }
        setTab(0);
    }, [event, open]);

    const handleInputChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

    const handleLatLngChange = (field) => (e) => {
        const value = e.target.value;
        const updated = { ...formData, [field]: value };
        setFormData(updated);
        const lat = parseFloat(field === "latitude" ? value : updated.latitude);
        const lng = parseFloat(field === "longitude" ? value : updated.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
            setMapCenter([lat, lng]); setMarkerPosition([lat, lng]); setMapKey(p => p + 1);
        }
    };

    const handleSearchLocation = () => {
        if (!searchQuery.trim()) return;
        setIsGeocoding(true);
        fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`)
            .then(r => r.json()).then(data => {
                if (data && data.length > 0) {
                    const { lat, lon, display_name } = data[0];
                    const pos = [parseFloat(lat), parseFloat(lon)];
                    setMapCenter(pos); setMarkerPosition(pos);
                    setFormData({ ...formData, latitude: lat, longitude: lon });
                    setSearchQuery(display_name); setMapKey(p => p + 1);
                }
            }).catch(() => {}).finally(() => setIsGeocoding(false));
    };

    const handleMapPositionChange = (pos) => {
        setMarkerPosition(pos);
        setFormData({ ...formData, latitude: pos[0].toFixed(6), longitude: pos[1].toFixed(6) });
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploadingImage(true);
        for (const file of files) {
            try {
                const fd = new FormData(); fd.append("image", file);
                const token = localStorage.getItem("token");
                const res = await axios.post(`${API_URL}/upload`, fd, {
                    headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
                });
                if (res.data.status) {
                    setImages(prev => [...prev, {
                        url: res.data.data.url, filename: res.data.data.filename,
                        originalName: res.data.data.originalName, category: pendingCategory,
                    }]);
                }
            } catch (err) { console.error("Image upload error:", err); }
        }
        setUploadingImage(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleRemoveImage = async (index) => {
        const img = images[index];
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_URL}/upload/${img.filename}`, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) {}
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageCategoryChange = (index, cat) => {
        setImages(prev => prev.map((img, i) => (i === index ? { ...img, category: cat } : img)));
    };

    const handleSave = () => {
        if (!formData.conflictName || !formData.country || !formData.dateStart) {
            alert("Please fill in: Conflict Name, Country, and Start Date");
            return;
        }
        // Map totalDeaths to deathUnknown for backward compat
        const saveData = {
            ...formData,
            deathA: 0, deathB: 0, deathCivilians: 0,
            deathUnknown: parseInt(formData.totalDeaths) || 0,
            highImpactIntel: formData.highImpactIntel,
            weapons: eventWeapons,
            images,
            markers: eventMarkers,
            latitude: eventMarkers.length > 0 ? eventMarkers[0].lat : formData.latitude,
            longitude: eventMarkers.length > 0 ? eventMarkers[0].lng : formData.longitude,
        };
        delete saveData.totalDeaths;
        onSave(saveData);
        onClose();
    };

    const categoryColor = (cat) => {
        if (cat === "Incident Image") return "error";
        if (cat === "Satellite Image") return "info";
        return "secondary";
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: "#1e1e1e", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "2px" } }}>
            <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", pb: 1.5 }}>
                <MDTypography variant="h5" sx={{ color: "#fff !important", fontSize: "16px", fontWeight: 400, letterSpacing: "1px", textTransform: "uppercase" }}>{event ? "Edit event" : "Add event"}</MDTypography>
            </DialogTitle>

            <MDBox sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", px: 3, display: "flex", gap: 0 }}>
                {["Event Details", "Media & Location", "Weapons"].map((t, i) => (
                    <span key={t} onClick={() => setTab(i)} style={{
                        padding: "8px 14px", cursor: "pointer", fontSize: "11px", textTransform: "uppercase",
                        letterSpacing: "1px", color: tab === i ? "#fff" : "#666",
                        borderBottom: tab === i ? "1px solid #C29717" : "1px solid transparent",
                        marginBottom: "-1px"
                    }}>{t}</span>
                ))}
            </MDBox>

            <DialogContent onWheel={(e) => e.stopPropagation()} sx={{ minHeight: "420px" }}>
                {/* ===== TAB 1: Event Details ===== */}
                {tab === 0 && (
                    <MDBox mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <MDInput type="text" label="Event / Incident Title *" InputLabelProps={{ style: { color: "#999" } }} fullWidth
                                    value={formData.conflictName} onChange={handleInputChange("conflictName")} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete options={COUNTRIES_LIST} value={formData.country}
                                    onChange={(_, v) => { const dt = v ? getLocalDateTime(v) : formData.dateStart; setFormData({ ...formData, country: v, dateStart: dt || formData.dateStart }) }}
                                    renderInput={(params) => <TextField {...params} label="Country *" variant="outlined" size="medium" sx={{ height: "50px" }} />} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <MDInput select label="Type of Violence" fullWidth value={formData.typeOfViolence}
                                    onChange={handleInputChange("typeOfViolence")} InputLabelProps={{ shrink: true }}
                                    InputProps={{ style: { height: "40px" } }} SelectProps={{ style: { height: "40px" } }} sx={{ height: "40px" }}>
                                    {VIOLENCE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MDInput select label="Source" fullWidth value={formData.typeOfSource}
                                    onChange={handleInputChange("typeOfSource")} InputLabelProps={{ shrink: true }}
                                    InputProps={{ style: { height: "40px" } }} SelectProps={{ style: { height: "40px" } }} sx={{ height: "40px" }}>
                                    {SOURCE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </MDInput>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <MDInput type="text" label="Exact Sources" fullWidth value={formData.exactSources}
                                    onChange={handleInputChange("exactSources")} placeholder="Reuters, BBC News, etc." />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MDInput type="url" label="Technical Report URL" fullWidth value={formData.technicalReportUrl}
                                    onChange={handleInputChange("technicalReportUrl")} placeholder="https://forteintel.com/satellites/up/..." />
                            </Grid>

                            <Grid item xs={12}>
                                <MDInput type="text" label="Description" fullWidth multiline rows={3}
                                    value={formData.description} onChange={handleInputChange("description")} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField type="datetime-local" label="Date Start *" value={formData.dateStart}
                                    onChange={handleInputChange("dateStart")} variant="outlined" fullWidth InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField type="datetime-local" label="Date End" value={formData.dateEnd}
                                    onChange={handleInputChange("dateEnd")} variant="outlined" fullWidth InputLabelProps={{ shrink: true }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <MDInput type="text" label="Side A" fullWidth value={formData.sideA}
                                    onChange={handleInputChange("sideA")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MDInput type="text" label="Side B" fullWidth value={formData.sideB}
                                    onChange={handleInputChange("sideB")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MDInput type="number" label="Total Deaths" fullWidth value={formData.totalDeaths}
                                    onChange={handleInputChange("totalDeaths")} InputProps={{ inputProps: { min: 0 } }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <MDInput select label="Status" fullWidth value={formData.status}
                                    onChange={handleInputChange("status")} InputLabelProps={{ shrink: true }}
                                    InputProps={{ style: { height: "40px" } }} SelectProps={{ style: { height: "40px" } }} sx={{ height: "40px" }}>
                                    {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </MDInput>
                            </Grid>

                        </Grid>
                    </MDBox>
                )}

                {/* ===== TAB 2: Media & Location ===== */}
                {tab === 1 && (
                    <MDBox mt={2}>
                        {/* Images */}
                        <MDTypography variant="h6" fontWeight="medium" mb={2} sx={{ color: "#fff !important" }}>Images</MDTypography>

                        <Grid container spacing={2} alignItems="center" mb={2}>
                            <Grid item xs={12} md={4}>
                                <MDInput select label="Category" fullWidth value={pendingCategory}
                                    onChange={(e) => setPendingCategory(e.target.value)} InputLabelProps={{ shrink: true }}
                                    InputProps={{ style: { height: "40px" } }} SelectProps={{ style: { height: "40px" } }} sx={{ height: "40px" }}>
                                    {IMAGE_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" multiple onChange={handleImageUpload} />
                                <MDButton variant="gradient" color="info" fullWidth disabled={uploadingImage}
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    startIcon={uploadingImage ? <CircularProgress size={16} color="inherit" /> : <Icon>cloud_upload</Icon>}>
                                    {uploadingImage ? "Uploading..." : "Upload Image(s)"}
                                </MDButton>
                            </Grid>
                        </Grid>

                        {images.length > 0 ? (
                            <Grid container spacing={2} mb={3}>
                                {images.map((img, i) => (
                                    <Grid item xs={12} sm={6} md={4} key={i}>
                                        <MDBox sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", backgroundColor: "#fafafa" }}>
                                            <MDBox sx={{ width: "100%", height: "140px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0" }}>
                                                <img src={`${window.location.origin}${img.url}`} alt={img.originalName || "image"}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            </MDBox>
                                            <MDBox p={1}>
                                                <MDTypography variant="caption" color="text" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mb: 0.5 }}>
                                                    {img.originalName || img.filename}
                                                </MDTypography>
                                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                    <MDInput select value={img.category} onChange={(e) => handleImageCategoryChange(i, e.target.value)}
                                                        InputProps={{ style: { height: "30px", fontSize: "11px" } }}
                                                        SelectProps={{ style: { height: "30px", fontSize: "11px" } }}
                                                        sx={{ height: "30px", minWidth: "130px" }} size="small">
                                                        {IMAGE_CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontSize: "11px" }}>{c}</MenuItem>)}
                                                    </MDInput>
                                                    <IconButton size="small" onClick={() => handleRemoveImage(i)} sx={{ color: "#f44336" }}><Icon>delete</Icon></IconButton>
                                                </MDBox>
                                                <Chip label={img.category} size="small" color={categoryColor(img.category)} sx={{ fontSize: "10px", height: "18px", mt: 0.5 }} />
                                            </MDBox>
                                        </MDBox>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <MDBox sx={{ border: "2px dashed #ccc", borderRadius: "8px", p: 2.5, textAlign: "center", cursor: "pointer",
                                "&:hover": { borderColor: "#999", backgroundColor: "#fafafa" }, mb: 3 }}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                <Icon sx={{ fontSize: "36px", color: "#ccc", mb: 0.5 }}>add_photo_alternate</Icon>
                                <MDTypography variant="body2" color="text">Click to upload images</MDTypography>
                                <MDTypography variant="caption" color="text">JPG, PNG, GIF, WebP (max 10MB)</MDTypography>
                            </MDBox>
                        )}

                        {/* Geo markers */}
                        <MDTypography variant="h6" fontWeight="medium" mb={1} sx={{ color: "#fff !important" }}>Geo markers</MDTypography>
                        <MDBox display="flex" gap={1} mb={1.5}>
                            <div style={{ flex: 1, position: "relative" }}>
                                <input type="text" placeholder="Search location..." value={locSearch}
                                    onChange={e => { setLocSearch(e.target.value); if(locSearchTimeout.current)clearTimeout(locSearchTimeout.current); if(e.target.value.trim().length<3){setLocSuggestions([]);return;} locSearchTimeout.current=setTimeout(async()=>{try{const r=await fetch("https://nominatim.openstreetmap.org/search?format=json&limit=8&q="+encodeURIComponent(e.target.value));setLocSuggestions(await r.json());}catch(er){setLocSuggestions([]);}},400); }}
                                    onKeyDown={e => { if(e.key==="Enter"){e.preventDefault(); setSearchingLoc(true); fetch("https://nominatim.openstreetmap.org/search?format=json&q="+encodeURIComponent(locSearch)).then(r=>r.json()).then(d=>{if(d&&d.length>0){setEventMarkers(prev=>[...prev,{lat:parseFloat(d[0].lat),lng:parseFloat(d[0].lon),label:d[0].display_name.split(",")[0]}]);setLocSearch("");}}).finally(()=>setSearchingLoc(false));}}}
                                    style={{ width: "100%", background: "#2a2a2a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "2px", padding: "6px 10px", fontSize: "12px", color: "#fff", outline: "none" }} />
                                {locSuggestions.length > 0 && (
                                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#252525", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "2px", zIndex: 9999, maxHeight: "350px", overflowY: "auto", marginTop: "2px" }}>
                                        {locSuggestions.map((s, i) => (
                                            <div key={i} onClick={() => { setEventMarkers(prev => [...prev, {lat:parseFloat(s.lat),lng:parseFloat(s.lon),label:s.display_name.split(",")[0]}]); setLocSearch(""); setLocSuggestions([]); }}
                                                style={{ padding: "8px 10px", fontSize: "11px", color: "#ccc", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                                                onMouseEnter={e => e.target.style.background="rgba(194,151,23,0.1)"} onMouseLeave={e => e.target.style.background="transparent"}>
                                                {s.display_name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span onClick={() => { const lat=prompt("Latitude:"); const lng=prompt("Longitude:"); const label=prompt("Label:"); if(lat&&lng) setEventMarkers(prev=>[...prev,{lat:parseFloat(lat),lng:parseFloat(lng),label:label||""}]); }}
                                style={{ padding: "6px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "2px", color: "#aaa", fontSize: "11px", cursor: "pointer", whiteSpace: "nowrap" }}>
                                Lat/Lng
                            </span>
                        </MDBox>
                        {eventMarkers.length > 0 && (
                            <MDBox mb={1.5}>
                                {eventMarkers.map((mk, i) => (
                                    <MDBox key={i} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.6, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <Icon sx={{ color: "#C29717", fontSize: "14px !important" }}>drag_indicator</Icon>
                                            <span style={{ color: "#fff", fontSize: "12px" }}>{mk.label || "Marker " + (i+1)}</span>
                                            <span style={{ color: "#666", fontSize: "10px" }}>{mk.lat.toFixed(5)}, {mk.lng.toFixed(5)}</span>
                                        </MDBox>
                                        <span style={{ color: "#e55", cursor: "pointer", fontSize: "13px" }} onClick={() => setEventMarkers(prev => prev.filter((_,idx)=>idx!==i))}>x</span>
                                    </MDBox>
                                ))}
                            </MDBox>
                        )}
                        {eventMarkers.length > 0 && (
                            <MDBox sx={{ height: "300px", width: "100%", borderRadius: "4px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", }}>
                                <MapContainer
                                    key={"markers-" + eventMarkers.length}
                                    center={[eventMarkers[0].lat, eventMarkers[0].lng]}
                                    zoom={10}
                                    style={{ height: "100%", width: "100%" }}
                                    attributionControl={false}
                                >
                                    <LayerSwitcher />
                                    <FitBoundsOnce markers={eventMarkers} />
                                    {eventMarkers.map((mk, i) => (
                                        <Marker
                                            key={i}
                                            position={[mk.lat, mk.lng]}
                                            draggable={true}
                                            icon={L.divIcon({
                                                className: "",
                                                html: '<div style="width:14px;height:14px;border-radius:50%;background:#C29717;border:2px solid rgba(255,255,255,0.5);transform:translate(-7px,-7px);cursor:grab"></div>',
                                                iconSize: [0, 0],
                                                iconAnchor: [0, 0],
                                            })}
                                            eventHandlers={{
                                                dragend: (e) => {
                                                    const { lat, lng } = e.target.getLatLng();
                                                    const newLat = parseFloat(lat.toFixed(5));
                                                    const newLng = parseFloat(lng.toFixed(5));
                                                    setEventMarkers(prev => prev.map((m, idx) =>
                                                        idx === i ? { ...m, lat: newLat, lng: newLng } : m
                                                    ));
                                                    // Reverse geocode to update label
                                                    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLat}&lon=${newLng}`, { headers: { "User-Agent": "FORTE-Intel/1.0" } })
                                                        .then(r => r.json())
                                                        .then(d => {
                                                            if (d && d.display_name) {
                                                                setEventMarkers(prev => prev.map((m, idx) =>
                                                                    idx === i ? { ...m, label: d.display_name.split(",")[0] } : m
                                                                ));
                                                            }
                                                        }).catch(() => {});
                                                },
                                            }}
                                        >
                                            {mk.label && (
                                                <Tooltip permanent direction="right" offset={[10, 0]}
                                                    className="marker-label-tooltip">
                                                    <span style={{ background: "rgba(26,26,26,0.85)", border: "1px solid rgba(194,151,23,0.4)", color: "#C29717", fontSize: "10px", padding: "1px 5px", borderRadius: "2px", fontFamily: "Inter, sans-serif" }}>
                                                        {mk.label}
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Marker>
                                    ))}
                                </MapContainer>
                            </MDBox>
                        )}
                    </MDBox>
                )}
                {tab === 2 && (
                    <MDBox mt={2}>
                        <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Weapons used</MDTypography>

                        <MDBox display="flex" gap={1} mb={1.5}>
                            <div style={{ flex: 1, position: "relative" }}>
                                <input type="text" placeholder="Search weapon..." value={weaponSearch}
                                    onChange={e => {
                                        setWeaponSearch(e.target.value);
                                        if (weaponSearchTimeout.current) clearTimeout(weaponSearchTimeout.current);
                                        if (e.target.value.length < 2) { setWeaponResults([]); return; }
                                        weaponSearchTimeout.current = setTimeout(async () => {
                                            try {
                                                const res = await axios.get(MODAL_API + '/arsenal/catalog');
                                                if (res.data.status) {
                                                    const q = e.target.value.toLowerCase();
                                                    setWeaponResults(res.data.data.filter(w => w.name.toLowerCase().includes(q) && !eventWeapons.find(ew => ew.name === w.name)).slice(0, 8));
                                                }
                                            } catch(err) {}
                                        }, 300);
                                    }}
                                    style={{ width: "100%", background: "#2a2a2a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "2px", padding: "6px 10px", fontSize: "12px", color: "#fff", outline: "none" }} />
                                {weaponResults.length > 0 && (
                                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#252525", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "2px", zIndex: 9999, maxHeight: "250px", overflowY: "auto", marginTop: "2px" }}>
                                        {weaponResults.map((w, i) => (
                                            <div key={i} onClick={() => { setEventWeapons([...eventWeapons, w]); setWeaponSearch(''); setWeaponResults([]); }}
                                                style={{ padding: "8px 10px", fontSize: "11px", color: "#ccc", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                                                onMouseEnter={e => e.target.style.background = "rgba(194,151,23,0.1)"}
                                                onMouseLeave={e => e.target.style.background = "transparent"}>
                                                <span style={{ color: "#fff" }}>{w.name}</span> <span style={{ color: "#888", fontSize: "10px" }}>{w.designation} - {w.operator} - {w.role}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span onClick={() => setShowAddWeapon(!showAddWeapon)} style={{ padding: "6px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "2px", color: "#aaa", fontSize: "11px", cursor: "pointer", whiteSpace: "nowrap" }}>+ New</span>
                        </MDBox>

                        {showAddWeapon && (
                            <MDBox sx={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: "2px", p: 1.5, mb: 1.5 }}>
                                <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                    <input placeholder="Name *" value={newWeapon.name} onChange={e => setNewWeapon({...newWeapon, name: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'150px'}} />
                                    <input placeholder="Designation" value={newWeapon.designation} onChange={e => setNewWeapon({...newWeapon, designation: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'120px'}} />
                                    <input placeholder="Manufacturer" value={newWeapon.manufacturer} onChange={e => setNewWeapon({...newWeapon, manufacturer: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'120px'}} />
                                    <input placeholder="Operator" value={newWeapon.operator} onChange={e => setNewWeapon({...newWeapon, operator: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'120px'}} />
                                    <select value={newWeapon.role} onChange={e => setNewWeapon({...newWeapon, role: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#ccc',outline:'none'}}>
                                        <option value="offensive">Offensive</option>
                                        <option value="defensive">Defensive</option>
                                    </select>
                                    <span onClick={async () => {
                                        if (!newWeapon.name) return;
                                        try {
                                            await axios.post(MODAL_API + '/arsenal/catalog', newWeapon);
                                            setEventWeapons([...eventWeapons, newWeapon]);
                                            setNewWeapon({name:'',designation:'',manufacturer:'',operator:'',role:'offensive'});
                                            setShowAddWeapon(false);
                                        } catch(e) {}
                                    }} style={{padding:'5px 12px',background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',color:'#C29717',fontSize:'11px',cursor:'pointer'}}>Add</span>
                                </MDBox>
                            </MDBox>
                        )}

                        {eventWeapons.length > 0 ? (
                            eventWeapons.map((w, i) => (
                                <MDBox key={i} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 0.8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <MDBox>
                                        <span style={{ color: "#fff", fontSize: "12px", fontWeight: 500 }}>{w.name}</span>
                                        <span style={{ color: "#C29717", fontSize: "9px", marginLeft: "6px" }}>{w.designation || ""}</span>
                                        <span style={{ color: "#888", fontSize: "10px", marginLeft: "8px" }}>{w.operator} - {w.role}</span>
                                    </MDBox>
                                    <span style={{ color: "#e55", cursor: "pointer", fontSize: "13px" }} onClick={() => setEventWeapons(eventWeapons.filter((_, idx) => idx !== i))}>x</span>
                                </MDBox>
                            ))
                        ) : (
                            <MDTypography sx={{ color: "#666", fontSize: "12px", textAlign: "center", py: 2 }}>No weapons assigned. Search above to add.</MDTypography>
                        )}
                    </MDBox>
                )}

            </DialogContent>

            <DialogActions sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", pt: 1.5 }}>
                <MDButton onClick={onClose} variant="outlined" color="secondary">Cancel</MDButton>
                {tab === 0 && (
                    <MDButton onClick={() => setTab(1)} variant="gradient" color="dark">
                        Next <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                    </MDButton>
                )}
                {tab === 1 && (
                    <>
                        <MDButton onClick={() => setTab(0)} variant="outlined" color="dark">
                            <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Back
                        </MDButton>
                        <MDButton onClick={() => setTab(2)} variant="gradient" color="dark">
                            Next <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                        </MDButton>
                    </>
                )}
                {tab === 2 && (
                    <>
                        <MDButton onClick={() => setTab(1)} variant="outlined" color="dark">
                            <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Back
                        </MDButton>
                        <MDButton onClick={handleSave} variant="gradient" color="info">SAVE</MDButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

EventModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    event: PropTypes.object,
    onSave: PropTypes.func.isRequired,
};
EventModal.defaultProps = { event: null };

export default EventModal;
